// Copyright (c) Docugami, Inc. All rights reserved.

import { Point } from "sdv/Types";
import { Box, TokenIdToBox } from "store/shared_types";
import { clamp } from "utils/MathUtils";
import { Page } from "./Page";

export interface WorldSpaceConfiguration {
    pageBreakHeight: number;
    width: number;
}

export const DefaultWorldSpaceConfiguration: WorldSpaceConfiguration = {
    pageBreakHeight: 20,
    width: 850
}

export function worldToViewSpace(point: Point, viewPosition: Point): Point {
    return {
        x: point.x - viewPosition.x,
        y: point.y - viewPosition.y
    };
}

export function transformTokenBoxesPageToWorldSpace(page: Page, tokenLocations: TokenIdToBox): TokenIdToBox {
    const transformed: TokenIdToBox = Object.fromEntries(Object.entries(tokenLocations).map(entry => {
        const [tokenId, box] = entry;
        const newBox = transformBoxPageToWorldSpace(page, box);
        return [tokenId, newBox];
    }));

    return transformed;
}

export function scaleValue(value: number, scalar: number): number {
    return Math.floor(value * scalar);
}

export function transformBoxPageToWorldSpace(page: Page, box: Box): Box {
    const xOffset = page.position.x;
    const yOffset = page.position.y;
    const scaleFactor = page.scaleFactor;

    const newBox: Box = {
        left: scaleValue(box.left, scaleFactor) + xOffset,
        right: scaleValue(box.right, scaleFactor) + xOffset,
        top: scaleValue(box.top, scaleFactor) + yOffset,
        bottom: scaleValue(box.bottom, scaleFactor) + yOffset,
        page: page.pageIndex
    }

    return newBox;
}

export function transformBoxWorldToViewSpace(box: Box, viewPosition: Point): Box {
    return {
        left: box.left - viewPosition.x,
        right: box.right - viewPosition.x,
        top: box.top - viewPosition.y,
        bottom: box.bottom - viewPosition.y,
        page: box.page
    }
}

export function transformPointWorldToViewSpace(point: Point, viewPosition: Point): Point {
    return {
        x: point.x - viewPosition.x,
        y: point.y - viewPosition.y
    }
}

export function transformPointDocumentToWorldSpace(
    point: Point,
    view: HTMLElement,
    scale: number,
    clampToView: boolean = true): Point {
    const rect = view.getBoundingClientRect();
    const x = point.x - rect.left;
    const y = point.y - rect.top;

    return {
        x: scaleValue(clampToView ? clamp(x, 0, rect.width) : x, scale),
        y: scaleValue((clampToView ? clamp(y, 0, rect.height) : y) + view.scrollTop, scale)
    };
}
